import { richTextToString } from './rich-text'
import { truncateText } from './truncate-text'

type Resolvable<T> = T | (() => T)

type SeoImageSize = {
  url?: string | null
  width?: number | null
  height?: number | null
}

type SeoImage = {
  url?: string | null
  alt?: string | null
  width?: number | null
  height?: number | null
  sizes?: Record<string, SeoImageSize | null> | null
} | null

export type PageSeoInput = {
  // the page's own title -- rendered as `<title>` through the app-wide
  // titleTemplate, and as og:title with the site name appended by hand
  // (a titleTemplate only ever applies to `<title>`)
  title?: Resolvable<string | null | undefined>
  // plain text or a lexical rich-text object; falls back to the shared global's
  // seoDescription when the page has nothing of its own
  description?: Resolvable<string | unknown | null | undefined>
  // a media doc; falls back to the shared global's coverImage
  image?: Resolvable<SeoImage | undefined>
  type?: 'website' | 'article'
}

const resolve = <T>(value: Resolvable<T> | undefined): T | undefined =>
  typeof value === 'function' ? (value as () => T)() : value

// Facebook/LinkedIn/WhatsApp all reject a relative og:image, so every url has to
// leave here absolute. In production payload hands out absolute CDN urls
// already; locally they are paths on the payload server.
const toAbsoluteUrl = (url: string | null | undefined, origin: string) => {
  if (!url) return undefined
  if (/^https?:\/\//.test(url)) return url
  return `${origin.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`
}

const toPlainText = (value: unknown): string | undefined => {
  if (!value) return undefined
  if (typeof value === 'string') return value.trim() || undefined
  // lexical rich text
  const text = richTextToString(value)
  return text || undefined
}

// ~2 lines in a search result / share card; anything longer is cut off anyway
const DESCRIPTION_WORD_LIMIT = 30

export const useSiteName = () => {
  const sharedData = useState<any>('sharedData')
  return computed<string>(() => sharedData.value?.siteName || 'de ateliers')
}

/**
 * The single place page-level meta tags are set. Pages pass what they have --
 * everything they don't have falls back to the `shared` global in payload.
 *
 * Pass getters (`() => data.value?.title`) rather than values so the tags keep
 * up with a locale switch, which refetches in place without remounting.
 */
export const usePageSeo = (input: PageSeoInput = {}) => {
  const config = useRuntimeConfig()
  const sharedData = useState<any>('sharedData')
  const siteName = useSiteName()
  const requestUrl = useRequestURL()

  const origin = computed(() =>
    (config.public.frontendUrl as string | undefined)?.replace(/\/$/, '') || requestUrl.origin
  )

  // titles come straight from the CMS, where a trailing space is easy to leave
  // behind and would show up as "nieuws  - de ateliers"
  const title = computed(() => resolve(input.title)?.trim() || undefined)

  const description = computed(() => {
    const own = toPlainText(resolve(input.description))
    const text = own || toPlainText(sharedData.value?.seoDescription)
    return text ? truncateText(text, DESCRIPTION_WORD_LIMIT) : undefined
  })

  const image = computed<SeoImage | undefined>(
    () => resolve(input.image) || sharedData.value?.coverImage || null
  )

  // the large size is the one sized for a share card; the original can be tens
  // of megabytes, which crawlers drop
  const imageSource = computed(() => {
    const media = image.value
    if (!media) return undefined
    const large = media.sizes?.large
    const source = large?.url ? large : media
    const url = toAbsoluteUrl(source.url, origin.value)
    if (!url) return undefined
    return {
      url,
      width: source.width ?? undefined,
      height: source.height ?? undefined,
      alt: media.alt ?? undefined,
    }
  })

  // og:url, og:locale and og:locale:alternate are not set here: @nuxtjs/i18n
  // already emits all three from `useLocaleHead` in app.vue, alongside the
  // canonical and hreflang links
  // the title is registered on its own and only when there is one, so that a
  // page without one falls through to the app-wide titleTemplate rather than
  // rendering an empty `<title>`
  useHead(() => (title.value ? { title: title.value } : {}))

  useSeoMeta({
    description: () => description.value ?? null,

    ogTitle: () => (title.value ? `${title.value} - ${siteName.value}` : siteName.value),
    ogDescription: () => description.value ?? null,
    ogType: () => input.type ?? 'website',
    ogSiteName: () => siteName.value,
    ogImage: () => imageSource.value?.url ?? null,
    ogImageWidth: () => imageSource.value?.width ?? null,
    ogImageHeight: () => imageSource.value?.height ?? null,
    ogImageAlt: () => imageSource.value?.alt ?? null,

    twitterCard: () => (imageSource.value ? 'summary_large_image' : 'summary'),
    twitterTitle: () => (title.value ? `${title.value} - ${siteName.value}` : siteName.value),
    twitterDescription: () => description.value ?? null,
    twitterImage: () => imageSource.value?.url ?? null,
    twitterImageAlt: () => imageSource.value?.alt ?? null,
  })
}
